/**
 * Operator Status Widget
 *
 * Shows current status of all operators with their
 * tasks and performance indicators.
 * Connected to live Dashboard API with mock fallback.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import React from 'react';
import {
  User,
  PackageSearch,
  Truck,
  ArrowDownToLine,
  ArrowLeftRight,
  Coffee,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { WidgetInstance, OperatorActivity } from '../../types';
import { getOperatorActivities } from '../../mockDataService';
import { fetchOperatorStatus } from '../../../../../../services/dashboardService';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useTranslation } from '../../../../../../contexts/I18nProvider';
import WidgetWrapper from '../WidgetWrapper';
import { WidgetSkeleton } from '../shared';

interface OperatorStatusWidgetProps {
  instance: WidgetInstance;
}

const OperatorStatusWidget: React.FC<OperatorStatusWidgetProps> = ({ instance }) => {
  const { t } = useTranslation();
  const { data, isLoading, error, refresh, isFirstLoad, isLive } = useWidgetData<OperatorActivity[]>({
    widgetId: 'operator-status',
    instance,
    fetchFn: async () => {
      try {
        return await fetchOperatorStatus();
      } catch {
        return getOperatorActivities();
      }
    },
  });

  if (isFirstLoad) {
    return (
      <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={true}>
        <WidgetSkeleton variant="list" count={5} />
      </WidgetWrapper>
    );
  }

  const operators = data ?? [];

  const getTaskIcon = (type: OperatorActivity['taskType']) => {
    switch (type) {
      case 'picking':
        return <PackageSearch className="w-4 h-4" />;
      case 'receiving':
        return <Truck className="w-4 h-4" />;
      case 'putaway':
        return <ArrowDownToLine className="w-4 h-4" />;
      case 'transfer':
        return <ArrowLeftRight className="w-4 h-4" />;
      case 'idle':
        return <Coffee className="w-4 h-4" />;
    }
  };

  const getTaskColor = (type: OperatorActivity['taskType']) => {
    switch (type) {
      case 'picking':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'receiving':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'putaway':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'transfer':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'idle':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getPerformanceColor = (performance: number) => {
    if (performance >= 110) return 'text-green-400';
    if (performance >= 90) return 'text-white';
    return 'text-yellow-400';
  };

  return (
    <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={isLoading} error={error?.message} isLive={isLive}>
      <div className="h-full overflow-y-auto space-y-2 pr-1">
        {operators.map((operator) => (
          <div
            key={operator.operatorId}
            className={`
              flex items-center gap-3 p-2 rounded-lg border
              ${getTaskColor(operator.taskType)}
            `}
          >
            {/* Avatar */}
            <div className="flex-shrink-0 w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-gray-300" />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-white truncate">
                  {operator.operatorName}
                </span>
                <span className={`flex items-center gap-0.5 text-xs ${getPerformanceColor(operator.performance)}`}>
                  {operator.performance >= 100 ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  {operator.performance}%
                </span>
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                {getTaskIcon(operator.taskType)}
                <span className="text-xs truncate">{operator.currentTask}</span>
              </div>
            </div>

            {/* Lines processed */}
            <div className="text-right flex-shrink-0">
              <div className="text-lg font-bold text-white">{operator.linesProcessed}</div>
              <div className="text-xs text-gray-400">{t('manager.modules.wmsDashboard.operatorStatus.lines', {}, 'linjer')}</div>
            </div>
          </div>
        ))}

        {operators.length === 0 && !isLoading && (
          <div className="h-full flex items-center justify-center text-gray-500 text-sm">
            {t('manager.modules.wmsDashboard.operatorStatus.noActiveOperators', {}, 'Ingen aktive operatorer')}
          </div>
        )}
      </div>
    </WidgetWrapper>
  );
};

export default OperatorStatusWidget;
